import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ContentCopyOutlined,
  FileDownloadOutlined,
  MenuBookOutlined,
  OpenInNew,
  PhoneIphoneOutlined,
} from '@mui/icons-material';
import { QRCodeCanvas, QRCodeSVG } from 'qrcode.react';
import JSZip from 'jszip';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PageHeader } from '../../components/page-header/PageHeader';
import { PAGE_GUTTER } from '../../core/theme/layout';
import { labelAboveSx } from '../../core/theme/formStyles';
import { useAccessibleLocations } from '../../hooks/useAccessibleLocations';
import { RestaurantService } from '../../services/restaurant/restaurantService';
import { hasRole } from '../../utils/roles';

/**
 * Dwa kody na restauracje, oba po slugu:
 * - menu.menusso.com/{slug} — menu w przegladarce (menusso-core-menu),
 * - get.menusso.com/{slug} — Worker store-redirect z menusso-core-customer-app, ktory
 *   odsyla do App Store / Google Play. Restauracja bez aplikacji nie ma wpisu w
 *   Workerze i dostaje 404 — wtedy karta pokazuje kod jako "w przygotowaniu".
 *   Przy `customerAppEnabled = false` aplikacji nie bedzie wcale, wiec karty nie ma.
 */
type QrKind = 'menu' | 'app';

const QR_KINDS: {
  key: QrKind;
  baseUrl: string;
  icon: React.ReactElement;
  checkReady?: boolean;
  requiresCustomerApp?: boolean;
}[] = [
  { key: 'menu', baseUrl: 'https://menu.menusso.com', icon: <MenuBookOutlined /> },
  {
    key: 'app',
    baseUrl: 'https://get.menusso.com',
    icon: <PhoneIphoneOutlined />,
    checkReady: true,
    requiresCustomerApp: true,
  },
];

// H (~30% nadmiarowosci): kod skanuje sie mimo zagiecia, zabrudzenia czy odblasku na
// laminowanej karcie przy stoliku.
const QR_LEVEL = 'H';
// Bialy margines (quiet zone) w modulach — 4 to minimum ze specyfikacji QR. Bez niego
// kod wklejony na ciemne tlo w Canvie czy Wordzie przestaje sie skanowac.
const QR_MARGIN = 4;
// Bok PNG w pikselach — starcza na wydruk ok. 8 cm w 300 dpi.
const PNG_SIZE = 1024;
const PREVIEW_SIZE = 240;

const restaurantService = new RestaurantService();

type LinkStatus = 'checking' | 'ready' | 'notReady';

// Pyta Worker, czy zna adres. `?p=other` wymusza strone zamiast przekierowania do
// sklepu, a Worker odpowiada z CORS, wiec status jest czytelny. Za "niegotowy"
// uznajemy tylko jawne 404 — blad sieci czy stary Worker bez CORS nie moze
// oznaczyc dzialajacego kodu jako zepsutego.
const useLinkStatus = (url: string | null, enabled: boolean): LinkStatus => {
  const [status, setStatus] = React.useState<LinkStatus>('checking');

  React.useEffect(() => {
    if (!enabled || !url) {
      setStatus('ready');
      return;
    }
    const controller = new AbortController();
    setStatus('checking');
    fetch(`${url}?p=other`, { method: 'HEAD', cache: 'no-store', signal: controller.signal })
      .then((res) => setStatus(res.status === 404 ? 'notReady' : 'ready'))
      .catch(() => {
        if (!controller.signal.aborted) setStatus('ready');
      });
    return () => controller.abort();
  }, [url, enabled]);

  return status;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const canvasToPng = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob returned null'))),
      'image/png',
    ),
  );

// Podglad jest rozciagniety stylem na szerokosc karty. Plik dostaje staly rozmiar
// domyslny, zeby po otwarciu nie byl miniaturka; viewBox i tak skaluje go bez strat.
const svgToFile = (svg: SVGSVGElement) => {
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  clone.setAttribute('width', String(PNG_SIZE));
  clone.setAttribute('height', String(PNG_SIZE));
  clone.removeAttribute('style');
  const markup = new XMLSerializer().serializeToString(clone);
  return new Blob([`<?xml version="1.0" encoding="UTF-8"?>\n${markup}`], {
    type: 'image/svg+xml',
  });
};

export const QrCodesPage = () => {
  const { t } = useTranslation();
  const { locations, loading: locationsLoading } = useAccessibleLocations();
  // Wlasciciel i vendor maja jedna restauracje, wiec nigdy nie dostaja selecta —
  // nawet gdyby `location/list` oddal im wiecej niz jeden rekord.
  const canPickLocation = !hasRole('RESTAURANT_OWNER', 'VENDOR');
  const [locationId, setLocationId] = React.useState<number | null>(null);
  const [slug, setSlug] = React.useState<string | null>(null);
  const [customerAppEnabled, setCustomerAppEnabled] = React.useState(true);
  const [slugLoading, setSlugLoading] = React.useState(false);
  const [slugError, setSlugError] = React.useState(false);

  React.useEffect(() => {
    if (locationId === null && locations.length > 0) setLocationId(locations[0].id);
  }, [locations, locationId]);

  // Slug zmienia sie razem z nazwa restauracji, wiec czytamy go przy kazdym wejsciu,
  // a nie z cache listy lokalizacji.
  React.useEffect(() => {
    if (locationId === null) return;
    let cancelled = false;
    setSlug(null);
    setSlugError(false);
    setSlugLoading(true);
    restaurantService
      .getRestaurants(locationId)
      .then((data) => {
        if (cancelled) return;
        if (data?.slug) setSlug(data.slug);
        else setSlugError(true);
        // Brak pola (starszy backend) traktujemy jak domyslne true z bazy.
        setCustomerAppEnabled(data?.customerAppEnabled !== false);
      })
      .catch((error) => {
        if (cancelled) return;
        Sentry.captureException(error);
        setSlugError(true);
      })
      .finally(() => {
        if (!cancelled) setSlugLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId]);

  const locationName = locations.find((l) => l.id === locationId)?.name ?? '';

  return (
    <>
      <PageHeader title={t('qrCodes.title')} subtitle={t('qrCodes.subtitle')} />
      <Box sx={{ px: PAGE_GUTTER, pb: 'var(--space-10)', maxWidth: 1040 }}>
        {canPickLocation && locations.length > 1 && (
          <Box sx={{ pb: 'var(--space-5)', maxWidth: 400, ...labelAboveSx }}>
            <FormControl size="small" fullWidth>
              <InputLabel shrink>{t('qrCodes.location')}</InputLabel>
              <Select
                value={locationId ?? ''}
                onChange={(e) => setLocationId(Number(e.target.value))}
              >
                {locations.map((l) => (
                  <MenuItem key={l.id} value={l.id}>
                    {l.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {!locationsLoading && locations.length === 0 ? (
          <Alert severity="info">{t('qrCodes.noLocations')}</Alert>
        ) : slugError ? (
          <Alert severity="error">{t('qrCodes.loadFailed')}</Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 'var(--space-5)',
            }}
          >
            {QR_KINDS.filter((kind) => !kind.requiresCustomerApp || customerAppEnabled).map(
              (kind) => (
                <QrCard
                  key={kind.key}
                  kind={kind.key}
                  icon={kind.icon}
                  checkReady={kind.checkReady ?? false}
                  url={slug ? `${kind.baseUrl}/${slug}` : null}
                  loading={locationsLoading || slugLoading}
                  fileBase={`menusso-${kind.key}-${slug ?? ''}`}
                  locationName={locationName}
                />
              ),
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

interface QrCardProps {
  kind: QrKind;
  icon: React.ReactElement;
  checkReady: boolean;
  url: string | null;
  loading: boolean;
  fileBase: string;
  locationName: string;
}

const QrCard = ({
  kind,
  icon,
  checkReady,
  url,
  loading: dataLoading,
  fileBase,
  locationName,
}: QrCardProps) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const linkStatus = useLinkStatus(url, checkReady);
  const loading = dataLoading || linkStatus === 'checking';
  const notReady = linkStatus === 'notReady';
  const svgRef = React.useRef<SVGSVGElement>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [downloading, setDownloading] = React.useState(false);

  const handleDownload = async () => {
    if (!svgRef.current || !canvasRef.current) return;
    setDownloading(true);
    try {
      const zip = new JSZip();
      zip.file(`${fileBase}.svg`, svgToFile(svgRef.current));
      zip.file(`${fileBase}.png`, await canvasToPng(canvasRef.current));
      downloadBlob(await zip.generateAsync({ type: 'blob' }), `${fileBase}.zip`);
    } catch (error) {
      Sentry.captureException(error);
      notify(t('qrCodes.downloadFailed'), { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      notify(t('qrCodes.linkCopied'), { type: 'success' });
    } catch {
      notify(t('qrCodes.copyFailed'), { type: 'error' });
    }
  };

  return (
    <Box
      component="section"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        backgroundColor: 'var(--color-surface)',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ display: 'flex', gap: 'var(--space-4)', p: 'var(--space-5)' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 48,
            height: 48,
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--color-primary-soft)',
            color: 'var(--color-primary)',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 'var(--text-2xs)',
              fontWeight: 700,
              letterSpacing: 'var(--tracking-caps)',
              textTransform: 'uppercase',
              color: 'var(--color-primary)',
            }}
          >
            {t(`qrCodes.kinds.${kind}.eyebrow`)}
          </Typography>
          <Box
            sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--space-2)' }}
          >
            <Typography component="h3" sx={{ fontWeight: 700, fontSize: 'var(--text-md)' }}>
              {t(`qrCodes.kinds.${kind}.title`)}
            </Typography>
            {notReady && (
              <Box
                component="span"
                sx={{
                  px: 'var(--space-2)',
                  borderRadius: 'var(--radius-pill)',
                  backgroundColor: 'var(--color-warning-soft)',
                  color: 'var(--color-warning)',
                  fontSize: 'var(--text-2xs)',
                  fontWeight: 700,
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                {t('qrCodes.notReady.badge')}
              </Box>
            )}
          </Box>
          <Typography
            sx={{
              mt: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              lineHeight: 'var(--leading-normal)',
            }}
          >
            {t(`qrCodes.kinds.${kind}.description`)}
          </Typography>
        </Box>
      </Box>

      {/* Bialy kafel niezaleznie od motywu — tak kod wyglada na wydruku. Kod rozciaga sie
          na cala szerokosc karty (viewBox skaluje go bez strat), a bialy margines
          robi sama quiet zone, wiec kafel nie ma wlasnego paddingu. Klikniecie otwiera
          adres, pod ktory kod prowadzi. */}
      <Box sx={{ px: 'var(--space-5)', pb: 'var(--space-5)' }}>
        <Box
          component={url && !loading ? 'a' : 'div'}
          href={url && !loading ? url : undefined}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('qrCodes.openLink')}
          sx={{
            position: 'relative',
            display: 'block',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            backgroundColor: 'white',
            boxShadow: 'var(--shadow-xs)',
            overflow: 'hidden',
            lineHeight: 0,
            // Wyszarzony, zeby nikt nie wzial go za dzialajacy — ale nadal czytelny,
            // bo mozna go wydrukowac juz teraz.
            '& svg': notReady ? { opacity: 0.35, filter: 'grayscale(1)' } : undefined,
            transition: 'border-color var(--dur-fast), box-shadow var(--dur-fast)',
            '&[href]:hover, &[href]:focus-visible': {
              borderColor: 'var(--color-primary)',
              boxShadow: 'var(--shadow-focus)',
            },
          }}
        >
          {url && !loading ? (
            <QRCodeSVG
              ref={svgRef}
              value={url}
              level={QR_LEVEL}
              size={PREVIEW_SIZE}
              marginSize={QR_MARGIN}
              title={`${t(`qrCodes.kinds.${kind}.title`)} — ${locationName}`}
              style={{ width: '100%', height: 'auto' }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              sx={{ width: '100%', height: 'auto', aspectRatio: '1' }}
            />
          )}
          {/* Komunikat na wyszarzonym kodzie zamiast nad nim, zeby karta nie rosla. */}
          {notReady && !loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 'var(--space-5)',
                lineHeight: 'var(--leading-normal)',
                pointerEvents: 'none',
              }}
            >
              <Alert severity="warning" sx={{ boxShadow: 'var(--shadow-md)' }}>
                {t('qrCodes.notReady.description')}
              </Alert>
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          mt: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-3)',
          p: 'var(--space-5)',
          borderTop: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-bg-subtle)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
            minWidth: 0,
            pl: 'var(--space-3)',
            pr: 'var(--space-1)',
            minHeight: 40,
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            backgroundColor: 'var(--color-surface)',
          }}
        >
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 'var(--text-sm)',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              color: 'var(--color-text-secondary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {url ?? '…'}
          </Typography>
          <Tooltip title={t('qrCodes.copyLink')}>
            <span>
              <IconButton size="small" onClick={handleCopy} disabled={!url}>
                <ContentCopyOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t('qrCodes.openLink')}>
            <span>
              <IconButton
                size="small"
                component="a"
                href={url ?? undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!url}
              >
                <OpenInNew fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleDownload}
          disabled={!url || loading || downloading}
          startIcon={
            downloading ? <CircularProgress size={18} color="inherit" /> : <FileDownloadOutlined />
          }
        >
          {t('qrCodes.download')}
        </Button>
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: 'var(--text-xs)',
            color: 'var(--color-text-muted)',
          }}
        >
          {t('qrCodes.downloadHint')}
        </Typography>
      </Box>

      {/* Zrodlo PNG: osobny canvas w rozdzielczosci do druku, poza ekranem. */}
      {url && (
        <Box sx={{ display: 'none' }} aria-hidden>
          <QRCodeCanvas
            ref={canvasRef}
            value={url}
            level={QR_LEVEL}
            size={PNG_SIZE}
            marginSize={QR_MARGIN}
          />
        </Box>
      )}
    </Box>
  );
};

export default QrCodesPage;
